<header class="topbar">
  <button
    type="button"
    class="menu-toggle"
    [attr.aria-label]="'shared.nav.toggleMenu' | translate"
    [attr.aria-expanded]="layout.sidebarOpen()"
    (click)="layout.toggleSidebar()"
  >
    <app-icon name="menu" [size]="20" />
  </button>

  <ng-template #brandContent>
    @if (branding.logoUrl(); as logoUrl) {
      <img class="mark logo" [src]="logoUrl" alt="" />
    } @else {
      <img class="mark logo" src="/branding/app-logo.png" [alt]="'shared.logoAlt' | translate" />
    }
    <span class="org-name" [title]="organizationLabel">{{ organizationLabel }}</span>
  </ng-template>

  @if (hasOrgSwitcher) {
    <div class="brand org-switcher" #orgMenu (keydown.escape)="isOrgMenuOpen.set(false)">
      <button
        type="button"
        class="org-trigger"
        data-testid="org-switcher-trigger"
        aria-haspopup="true"
        [attr.aria-expanded]="isOrgMenuOpen()"
        [attr.aria-label]="'shared.orgSwitcher.label' | translate"
        (click)="toggleOrgMenu()"
      >
        <ng-container *ngTemplateOutlet="brandContent" />
        @if (auth.invitations().length > 0) {
          <span class="org-invitations-badge" data-testid="org-invitations-badge">{{ auth.invitations().length }}</span>
        }
        <app-icon name="chevron-down" [size]="12" />
      </button>
      @if (isOrgMenuOpen()) {
        <div class="org-panel" data-testid="org-switcher-panel">
          <div class="org-panel-title">{{ 'shared.orgSwitcher.title' | translate }}</div>
          @for (m of auth.organizations(); track m.membershipId) {
            <button
              type="button"
              class="org-option"
              data-testid="org-switcher-option"
              [class.active]="isActiveOrganization(m)"
              [attr.aria-current]="isActiveOrganization(m) ? 'true' : null"
              [disabled]="switching()"
              [style.border-inline-start-color]="m.primaryColor"
              (click)="switchOrganization(m)"
            >
              <span class="org-option-name">{{ m.organizationName }}</span>
              @if (isActiveOrganization(m)) {
                <span class="org-option-note">{{ 'shared.orgSwitcher.current' | translate }}</span>
              } @else {
                @if (m.organizationSuspended) {
                  <span class="org-option-note">{{ 'shared.orgSwitcher.suspended' | translate }}</span>
                }
                @if (unreadIn(m) > 0) {
                  <span
                    class="org-unread-badge"
                    data-testid="org-unread-badge"
                    [attr.aria-label]="'shared.orgSwitcher.unread' | translate: { count: unreadIn(m) }"
                    >{{ unreadIn(m) > 99 ? '99+' : unreadIn(m) }}</span
                  >
                }
              }
            </button>
          }
          @if (switchError()) {
            <p class="org-panel-error" role="alert">{{ switchError() }}</p>
          }
          <app-pending-invitations />
        </div>
      }
    </div>
  } @else {
    <div class="brand">
      <ng-container *ngTemplateOutlet="brandContent" />
    </div>
  }

  <div class="actions">
    <button
      type="button"
      class="lang-trigger"
      data-testid="lang-trigger"
      [attr.lang]="targetLocale"
      [attr.aria-label]="(targetLocale === 'ar' ? 'shared.langSwitch.toAr' : 'shared.langSwitch.toEn') | translate"
      [attr.title]="(targetLocale === 'ar' ? 'shared.langSwitch.toAr' : 'shared.langSwitch.toEn') | translate"
      (click)="switchLanguage()"
    >
      <app-icon name="globe" [size]="16" />
      <span class="lang-label">{{ localeLabel(targetLocale) }}</span>
    </button>

    @if (!narrow() && shortcuts().length > 0) {
      <nav class="shortcuts" [attr.aria-label]="'shared.nav.shortcuts' | translate" data-testid="header-shortcuts">
        @for (shortcut of shortcuts(); track shortcut.id) {
          <a
            class="shortcut"
            [routerLink]="shortcut.path"
            [class.active]="isShortcutActive(shortcut)"
            [attr.aria-current]="isShortcutActive(shortcut) ? 'page' : null"
            [attr.aria-label]="shortcut.labelKey | translate"
            [attr.title]="shortcut.labelKey | translate"
            [attr.data-testid]="'shortcut-' + shortcut.id"
          >
            <app-icon [name]="shortcut.icon" [size]="18" />
            @if (shortcut.id === 'approvals' && approvalsBadgeText(); as count) {
              <span class="badge" data-testid="shortcut-approvals-badge">{{ count }}</span>
            }
          </a>
        }
      </nav>
      <span class="header-divider" data-testid="header-divider" role="separator" aria-orientation="vertical"></span>
    }

    <div class="notifications-menu" #notificationsMenu>
      <button
        type="button"
        class="bell"
        data-testid="bell"
        [attr.aria-label]="'shared.notifications' | translate"
        [attr.title]="'shared.notifications' | translate"
        (click)="toggleNotifications()"
      >
        <app-icon name="bell" [size]="18" />
        @if (unreadCount() > 0) {
          <span class="badge">{{ unreadCount() }}</span>
        }
      </button>
      @if (isNotificationsOpen()) {
        <div class="notifications-panel">
          <div class="notifications-header">{{ 'shared.notificationsPanel.title' | translate }}</div>
          @if (loadingNotifications()) {
            <p class="notifications-empty">{{ 'shared.notificationsPanel.loading' | translate }}</p>
          } @else if (recentNotifications().length === 0) {
            <p class="notifications-empty">{{ 'shared.notificationsPanel.empty' | translate }}</p>
          } @else {
            <ul>
              @for (item of recentNotifications(); track item.id) {
                <li
                  [class.unread]="item.isUnread"
                  [class.clickable]="canOpen(item)"
                  [attr.tabindex]="canOpen(item) ? 0 : null"
                  [attr.role]="canOpen(item) ? 'button' : null"
                  (click)="canOpen(item) && openNotification(item)"
                  (keydown.enter)="canOpen(item) && openNotification(item)"
                >
                  <span class="notification-title">
                    {{ notificationTitle(item) }}
                    @if (entityRefLabel(item); as ref) {
                      <span class="notification-ref">{{ ref }}</span>
                    }
                  </span>
                  <span class="notification-time">{{ item.sentAt | localizedDate: 'dateTime' : dateLang() }}</span>
                </li>
              }
            </ul>
          }
        </div>
      }
    </div>

    <div class="help-menu" #helpMenu>
      <button
        type="button"
        class="bell"
        data-testid="help-trigger"
        aria-haspopup="true"
        [attr.aria-expanded]="isHelpOpen()"
        [attr.aria-label]="'help.menu.label' | translate"
        [attr.title]="'help.menu.label' | translate"
        (click)="toggleHelp()"
      >
        <app-icon name="help" [size]="18" />
      </button>
      @if (isHelpOpen()) {
        <div class="user-panel help-panel" role="menu">
          @if (tours.currentTour()) {
            <button type="button" role="menuitem" data-testid="help-explain" (click)="explainPage()">
              {{ 'help.menu.explain' | translate }}
            </button>
          }
          <button type="button" role="menuitem" data-testid="help-all-guides" (click)="openGuides()">
            {{ 'help.menu.allGuides' | translate }}
          </button>
          @if (setupChecklist.hasChecklist()) {
            <button type="button" role="menuitem" data-testid="help-setup-checklist" (click)="openChecklist()">
              {{ 'help.menu.setupChecklist' | translate }}
            </button>
          }
        </div>
      }
    </div>
    @defer (when tours.panelOpen()) {
      @if (tours.panelOpen()) {
        <app-help-guides-panel />
      }
    }

    <div class="user-menu" #userMenu>
      <button
        type="button"
        class="user-trigger"
        data-testid="user-trigger"
        aria-haspopup="true"
        [attr.aria-expanded]="isUserMenuOpen()"
        [attr.aria-label]="'shared.userMenu.label' | translate"
        [attr.title]="auth.currentUser()?.fullName"
        (click)="toggleUserMenu()"
      >
        <span class="avatar">{{ userInitials }}</span>
      </button>
      @if (isUserMenuOpen()) {
        <div class="user-panel">
          <div class="user-card" data-testid="user-menu-info">
            <span class="avatar large" aria-hidden="true">{{ userInitials }}</span>
            <div class="user-card-text">
              <span class="user-name" data-testid="user-menu-name"><bdi>{{ auth.currentUser()?.fullName }}</bdi></span>
              <span class="user-email" data-testid="user-menu-email"><bdi dir="ltr">{{ auth.currentUser()?.email }}</bdi></span>
              @if (orderedRoles.length > 0) {
                <app-role-chip-list class="user-roles" data-testid="user-menu-roles" [roles]="orderedRoles" [max]="2" />
              }
              <span class="user-org" data-testid="user-menu-organization"><bdi>{{ organizationLabel }}</bdi></span>
            </div>
          </div>
          <div class="menu-separator" role="separator"></div>
          @if (narrow()) {
            @for (shortcut of shortcuts(); track shortcut.id) {
              <a
                class="menu-shortcut"
                [routerLink]="shortcut.path"
                [class.active]="isShortcutActive(shortcut)"
                [attr.data-testid]="'menu-shortcut-' + shortcut.id"
                (click)="isUserMenuOpen.set(false)"
              >
                <app-icon [name]="shortcut.icon" [size]="15" />
                {{ shortcut.labelKey | translate }}
                @if (shortcut.id === 'approvals' && approvalsBadgeText(); as count) {
                  <span class="menu-badge" data-testid="menu-approvals-badge">{{ count }}</span>
                }
              </a>
            }
          }
          <a class="menu-link" routerLink="/profile" data-testid="user-menu-profile" [class.active]="isProfileActive()" (click)="isUserMenuOpen.set(false)">
            <app-icon name="user" [size]="15" />
            {{ 'shared.userMenu.profile' | translate }}
          </a>
          @if (auth.organizations().length > 1) {
            <button type="button" data-testid="user-menu-switch-org" (click)="openOrganizationSwitcher(); $event.stopPropagation()">
              <app-icon name="building" [size]="15" />
              {{ 'shared.userMenu.switchOrganization' | translate }}
            </button>
          }
          <div class="menu-separator" role="separator"></div>
          <button type="button" class="danger" data-testid="user-menu-sign-out" (click)="logout()">
            <app-icon name="log-out" [size]="15" />
            {{ 'shared.signOut' | translate }}
          </button>
        </div>
      }
    </div>
  </div>
</header>
